import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { api } from "../utils/api";

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}j`;
}

export default function ProfileVisitors() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState({ visits: [], total: 0, plan: "free", can_see_details: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const result = await api("/api/visits/my");
        setData(result);
      } catch (err) {
        console.error("Visits error:", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-white">
          👁 {t("visitors_title") || "Qui a visité mon profil"}
        </h2>
        <p className="mt-1 text-sm text-sensoria-text-dim">
          {data.total} {t("visitors_count") || "visites récentes"}
        </p>
      </div>

      {/* Upsell banner for free users */}
      {!data.can_see_details && data.total > 0 && (
        <div className="sensoria-card sensoria-glow text-center">
          <div className="mb-2 text-3xl">🔒</div>
          <h3 className="font-display text-lg font-bold text-white">
            {data.total} {data.total > 1 ? "personnes ont" : "personne a"} visité votre profil
          </h3>
          <p className="mt-1 text-sm text-sensoria-text-dim">
            Passez Premium pour découvrir qui s'intéresse à vous.
          </p>
          <button
            onClick={() => navigate("/premium")}
            className="sensoria-btn mt-4 px-6 py-2 text-sm"
          >
            💜 Voir mes visiteurs — 9.99€/mois
          </button>
        </div>
      )}

      {/* Visitors list */}
      {data.visits.length === 0 ? (
        <div className="py-16 text-center">
          <div className="mb-3 text-4xl">👁</div>
          <p className="text-sensoria-text-dim">Aucune visite récente.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.visits.map((visit) => {
            const v = visit.visitor;
            const isBlurred = v.blurred;

            return (
              <div
                key={visit.id}
                className={`sensoria-card flex items-center gap-3 transition-all ${
                  isBlurred ? "opacity-70" : "hover:sensoria-glow cursor-pointer"
                }`}
                onClick={() => !isBlurred && v.id && navigate(`/messages/${v.id}`)}
              >
                <div
                  className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-xl ${
                    isBlurred ? "bg-sensoria-surface blur-sm" : "bg-sensoria-accent/15"
                  }`}
                >
                  {v.avatar_emoji}
                </div>
                <div className="min-w-0 flex-1">
                  <span
                    className={`font-semibold ${
                      isBlurred ? "text-sensoria-muted blur-sm select-none" : "text-white"
                    }`}
                  >
                    {v.username}
                  </span>
                  <div className="flex items-center gap-2 text-xs text-sensoria-muted">
                    <span>{timeAgo(visit.visited_at)}</span>
                    {!isBlurred && v.city && <span>· 📍 {v.city}</span>}
                    {!isBlurred && v.age && <span>· {v.age} ans</span>}
                  </div>
                </div>
                {isBlurred && (
                  <span className="rounded-full bg-sensoria-accent/10 px-2.5 py-1 text-[10px] font-semibold text-sensoria-accent-light">
                    Premium
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
